import React, { useEffect, useState } from "react";
import { Star, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";
import { useChrome } from "@/lib/guChrome";

export const useSiteSettings = () => {
  const [settings, setSettings] = useState(() => ssgData("settings") || null);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);
  return settings;
};

const Stars = ({ rating, className = "" }) => (
  <span className={`inline-flex items-center gap-0.5 ${className}`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? "fill-current" : "opacity-30"}`}
      />
    ))}
  </span>
);

export const GoogleRatingBadge = () => {
  const { g, reviewCountLabel } = useChrome();
  const s = useSiteSettings();
  if (!s?.google_review_url) return null;
  const hasRating = Number(s.google_rating) > 0;

  return (
    <a
      href={s.google_review_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="google-rating-badge"
      className="inline-flex items-center gap-3 rounded-full border border-[#FDFBF7]/20 bg-[#FDFBF7]/5 hover:bg-[#FDFBF7]/10 hover:border-[#E07A5F]/60 px-4 py-2.5 transition-colors group"
    >
      {hasRating ? (
        <>
          <span className="font-display text-xl text-[#FDFBF7] leading-none" data-testid="google-rating-value">
            {Number(s.google_rating).toFixed(1)}
          </span>
          <span className="flex flex-col leading-tight">
            <Stars rating={s.google_rating} className="text-[#E2C044]" />
            <span className="text-[11px] text-[#FDFBF7]/60 mt-1">
              {s.google_review_count > 0 ? reviewCountLabel(s.google_review_count) : g("on Google")}
            </span>
          </span>
        </>
      ) : (
        <span className="text-sm text-[#FDFBF7]/80">{g("Review us on Google")}</span>
      )}
      <ArrowUpRight className="w-3.5 h-3.5 text-[#E07A5F] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
    </a>
  );
};

export const GoogleReviewCard = () => {
  const s = useSiteSettings();
  if (!s?.google_review_url) return null;
  const hasRating = Number(s.google_rating) > 0;

  return (
    <div
      data-testid="google-review-card"
      className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-10 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
      <div className="relative">
        <p className="eyebrow text-[#E2C044] mb-4">Rate your experience</p>
        {hasRating && (
          <div className="flex items-baseline gap-3 mb-4">
            <span className="font-display text-5xl leading-none">{Number(s.google_rating).toFixed(1)}</span>
            <span className="flex flex-col">
              <Stars rating={s.google_rating} className="text-[#E2C044]" />
              {s.google_review_count > 0 && (
                <span className="text-xs text-[#FDFBF7]/60 mt-1">
                  from {s.google_review_count} verified Google reviews
                </span>
              )}
            </span>
          </div>
        )}
        <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-snug">
          Were we able to help you or your family?
        </h2>
        <p className="text-[#FDFBF7]/75 mt-4 leading-relaxed max-w-lg">
          A few honest lines on Google help another family in Bhavnagar find treatment instead of
          staying silent. Please don't mention any private clinical details — a general note about
          your experience is enough.
        </p>
        <a
          href={s.google_review_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="google-review-cta"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium transition-colors"
        >
          Write a Google Review <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};
